/**
 * Login Page Component
 *
 * Displays login form with email/password inputs.
 * Uses React Hook Form with Yup validation and MUI components.
 * Integrates with RTK Query authApi for login mutation.
 * Redirects authenticated users to dashboard.
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
} from '@mui/material';
import { loginValidationSchema, LoginFormInputs } from '../validation/loginValidationSchema';
import { useLoginMutation } from '../api/authApi';
import {
  useIsAuthenticated,
  useAuthLoading,
  useAuthError,
  usePasswordChangeRequired,
} from '../hooks/useAuthHooks';

/**
 * LoginPage - Main login form page
 *
 * Features:
 * - Email and password input fields with validation
 * - Real-time error display
 * - Loading state during login
 * - Auto-redirect to dashboard if already authenticated
 * - Displays auth errors from backend
 */
const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const authLoading = useAuthLoading();
  const authError = useAuthError();
  const passwordChangeRequired = usePasswordChangeRequired();

  const [login, { isLoading: isMutationLoading }] = useLoginMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LoginFormInputs>({
    resolver: yupResolver(loginValidationSchema),
  });

  // Extract success message from location state (e.g., after password change)
  const successMessage = (location.state as any)?.message;

  /**
   * Auto-redirect based on authentication state
   * - If password change required: redirect to /change-password
   * - If authenticated and no password change: redirect to dashboard
   */
  useEffect(() => {
    if (isAuthenticated) {
      if (passwordChangeRequired) {
        navigate('/change-password', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, passwordChangeRequired, navigate]);

  /**
   * Handle form submission
   * Calls login mutation with email and password
   */
  const onSubmit = async (data: LoginFormInputs) => {
    try {
      await login(data).unwrap();
      // Login success - redirect handled by useEffect
      reset();
    } catch (err) {
      // Error state handled by authSlice and displayed via authError
      console.error('Login failed:', err);
    }
  };

  const isLoading = isMutationLoading || authLoading;

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        py: 4,
      }}
    >
      <Card
        sx={{
          width: '100%',
          boxShadow: 3,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Title */}
          <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
            TravelCRM Admin
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Zaloguj się do swojego konta, aby kontynuować
          </Typography>

          {/* Success Message (e.g., after password change) */}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {successMessage}
            </Alert>
          )}

          {/* Auth Error Alert */}
          {authError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {authError}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Email Input */}
            <TextField
              fullWidth
              label="Adres e-mail"
              type="email"
              placeholder="np. biuro@firma.pl"
              margin="normal"
              disabled={isLoading}
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
              autoComplete="email"
              autoFocus
            />

            {/* Password Input */}
            <TextField
              fullWidth
              label="Hasło"
              type="password"
              placeholder="Wpisz swoje hasło"
              margin="normal"
              disabled={isLoading}
              {...register('password')}
              error={!!errors.password}
              helperText={errors.password?.message}
              autoComplete="current-password"
            />

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={isLoading}
              sx={{ mt: 3, py: 1.5 }}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  Logowanie...
                </Box>
              ) : (
                'Zaloguj się'
              )}
            </Button>
          </form>

          {/* Footer Info */}
          <Typography variant="caption" color="textSecondary" sx={{ mt: 3, display: 'block' }}>
            Nie pamiętasz hasła?{' '}
            <Typography component="span" color="primary" sx={{ cursor: 'pointer' }}>
              Skontaktuj się z administratorem
            </Typography>
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default LoginPage;
